import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TITLES, TITLE_LABELS, STATUS_META } from "@/lib/constants";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

export default function Practitioners() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [title, setTitle] = useState("");
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/practitioners", { params: { q: q || undefined, title: title || undefined } });
      setRows(r.data);
    } catch {
      toast.error("Failed to load roster");
    }
  };
  useEffect(() => { load(); }, [title]);

  return (
    <div className="space-y-6" data-testid="practitioners-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="overline text-gray-500">Roster</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Practitioners</h1>
          <p className="text-sm text-gray-600 mt-2">
            Every MD, DO, CRNA, RN, LPN, NP, PA, and Surgical Tech in your ASC — each tied to their full credentialing file.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-practitioner-btn"
          className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 transition-colors"
        >
          <Plus size={14} weight="bold" /> New practitioner
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 border border-gray-300 bg-white px-3 py-2 w-[320px] max-w-full">
          <MagnifyingGlass size={14} className="text-gray-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name or NPI"
            className="flex-1 outline-none text-sm"
            data-testid="practitioner-search-input"
          />
        </div>
        <select
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border border-gray-300 bg-white px-3 py-2 text-sm"
          data-testid="practitioner-title-filter"
        >
          <option value="">All titles</option>
          {TITLES.map((t) => <option key={t} value={t}>{t} — {TITLE_LABELS[t]}</option>)}
        </select>
        <button onClick={load} className="border border-gray-300 bg-white px-3 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-gray-100" data-testid="apply-filters-btn">Apply</button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="practitioners-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
              <th className="px-6 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Specialty</th>
              <th className="px-4 py-3 font-semibold">NPI</th>
              <th className="px-4 py-3 font-semibold">Credentials</th>
              <th className="px-4 py-3 font-semibold">Worst Status</th>
              <th className="px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="7" className="p-12 text-center text-gray-500 text-sm">No practitioners yet. Load demo data from the dashboard or create a new practitioner.</td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} className="data-row border-b border-gray-100">
                <td className="px-6 py-3">
                  <div className="font-medium text-slate-900">{p.last_name}, {p.first_name}</div>
                  <div className="text-[11px] text-gray-500">{p.email || "—"}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.title}</td>
                <td className="px-4 py-3 text-gray-700">{p.specialty || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-600">{p.npi || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.credential_count}</td>
                <td className="px-4 py-3"><StatusBadge status={p.status_summary?.worst_status || "NO_DATE"} /></td>
                <td className="px-4 py-3">
                  <Link
                    to={`/practitioners/${p.id}`}
                    className="text-xs uppercase tracking-wider font-semibold text-slate-700 hover:text-slate-900"
                    data-testid={`open-practitioner-${p.id}`}
                  >
                    Open →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && <PractitionerModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

function PractitionerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", title: "MD", specialty: "", npi: "",
    email: "", phone: "", hire_date: "", employment_status: "ACTIVE",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/practitioners", payload);
      toast.success("Practitioner created");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="practitioner-modal">
      <div className="bg-white border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <div className="overline text-gray-500">New record</div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900 mt-0.5">Practitioner</h3>
          </div>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-gray-500 hover:text-slate-900"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="First name" required value={form.first_name} onChange={set("first_name")} testid="prac-first-name" />
          <Field label="Last name" required value={form.last_name} onChange={set("last_name")} testid="prac-last-name" />
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Title *</label>
            <select value={form.title} onChange={set("title")} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="prac-title">
              {TITLES.map((t) => <option key={t} value={t}>{t} — {TITLE_LABELS[t]}</option>)}
            </select>
          </div>
          <Field label="Specialty" value={form.specialty} onChange={set("specialty")} testid="prac-specialty" />
          <Field label="NPI" value={form.npi} onChange={set("npi")} testid="prac-npi" />
          <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="prac-email" />
          <Field label="Phone" value={form.phone} onChange={set("phone")} testid="prac-phone" />
          <Field label="Hire date" type="date" value={form.hire_date} onChange={set("hire_date")} testid="prac-hire-date" />
          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={saving} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 disabled:opacity-60" data-testid="prac-save-btn">{saving ? "Saving…" : "Create practitioner"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, type="text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">
        {label}{required && " *"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
      />
    </div>
  );
}
